import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CheckCircleOutline, LocalShipping, ReportProblemOutlined, TrendingUp } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import Layout from '../components/Layout';
import { Kpi, Legend, Panel, SERIES } from '../components/ui';
import { tokens } from '../theme';

const MonthlyInventory = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [inventoryData, setInventoryData] = useState({
    production: [],
    livraisons: [],
    stock: [],
    totals: { production: 0, livraisons: 0, stock_final: 0 }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInventoryData();
  }, [selectedMonth]); // eslint-disable-line

  const loadInventoryData = async () => {
    setLoading(true);
    try {
      const startDate = dayjs(selectedMonth).startOf('month').format('YYYY-MM-DD');
      const endDate = dayjs(selectedMonth).endOf('month').format('YYYY-MM-DD');

      // Appels API en parallèle
      const [productionResponse, stockResponse, movementsResponse] = await Promise.all([
        fetch(`/api/reports?start_date=${startDate}&end_date=${endDate}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/stock/lots-maturation`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/stock/movements-by-period?start_date=${startDate}&end_date=${endDate}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const productionData = productionResponse.ok ? await productionResponse.json() : { reports: [] };
      const stockData = stockResponse.ok ? await stockResponse.json() : [];
      const movementsData = movementsResponse.ok ? await movementsResponse.json() : [];

      // Calculer les totaux par type PBA
      const productionByType = {};
      const deliveriesByType = {};
      const stockByType = {};

      // Production du mois
      productionData.reports.forEach(report => {
        if (report.pba_produced) {
          Object.entries(report.pba_produced).forEach(([type, qty]) => {
            if (!isNaN(qty)) {
              productionByType[type] = (productionByType[type] || 0) + parseInt(qty);
            }
          });
        }
      });

      // Livraisons du mois
      movementsData.forEach(movement => {
        if (movement.type_operation === 'LIVRAISON') {
          deliveriesByType[movement.pba_type] = (deliveriesByType[movement.pba_type] || 0) + movement.quantite;
        }
      });

      // Stock total actuel (tous les PBA disponibles)
      stockData.forEach(lot => {
        const type = lot.pba_type;
        const qty = lot.quantite_restante || 0;
        stockByType[type] = (stockByType[type] || 0) + qty;
      });

      // Préparer les données pour les graphiques
      const allTypes = [...new Set([...Object.keys(productionByType), ...Object.keys(deliveriesByType), ...Object.keys(stockByType)])];
      
      const chartData = allTypes.map(type => ({
        type,
        production: productionByType[type] || 0,
        livraisons: deliveriesByType[type] || 0,
        stock: stockByType[type] || 0
      }));

      // PBA matures et avaries/sorties
      const pbaMaturesDuMois = stockData.filter(lot => {
        const maturationMonth = dayjs(lot.maturation_date).format('YYYY-MM');
        return maturationMonth === selectedMonth && lot.est_mature;
      }).reduce((sum, lot) => sum + (lot.quantite_restante || 0), 0);

      const avariesSorties = movementsData.reduce((sum, movement) => {
        if (['SORTIE_INTERNE', 'AVARIE_MATURE', 'AVARIE_IMMATURE'].includes(movement.type_operation)) {
          return sum + movement.quantite;
        }
        return sum;
      }, 0);

      const totals = {
        production: Object.values(productionByType).reduce((sum, val) => sum + val, 0),
        livraisons: Object.values(deliveriesByType).reduce((sum, val) => sum + val, 0),
        stock_final: Object.values(stockByType).reduce((sum, val) => sum + val, 0),
        pba_matures: pbaMaturesDuMois,
        avaries_sorties: avariesSorties
      };

      setInventoryData({
        production: chartData,
        livraisons: [],
        stock: chartData,
        totals
      });

    } catch (error) {
      console.error('Erreur chargement inventaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const t = inventoryData.totals;
  const rows = inventoryData.production;
  const stockRows = rows.filter((x) => x.stock > 0).sort((x, y) => y.stock - x.stock);
  const stockMax = Math.max(1, ...stockRows.map((x) => x.stock));
  const mois = dayjs(selectedMonth).locale('fr').format('MMMM YYYY');

  return (
    <Layout title="Inventaire mensuel" plain>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2.5 }}>
        <TextField type="month" size="small" label="Mois" value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" onClick={loadInventoryData} disabled={loading}>{loading ? 'Chargement…' : 'Actualiser'}</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi icon={TrendingUp} tint="navy" label="Production" value={t.production} sub={mois} /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={LocalShipping} tint="amber" label="Livraisons" value={t.livraisons} sub={mois} /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={CheckCircleOutline} tint="green" label="Devenus matures" value={t.pba_matures ?? 0} sub="Lots maturés ce mois" /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={ReportProblemOutlined} tint="red" label="Avaries + sorties" value={t.avaries_sorties ?? 0} sub="Sorties hors livraison"
          valueColor={t.avaries_sorties ? '#B4232A' : undefined} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={7}>
          <Panel title="Production et livraisons par type" action={<Legend items={[['Production', SERIES.production], ['Livraisons', SERIES.livraisons]]} />} sx={{ height: '100%' }}>
            {rows.length === 0 ? <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>Aucune donnée pour {mois}</Typography> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={2}>
                  <CartesianGrid vertical={false} stroke="#E6E9EF" />
                  <XAxis dataKey="type" tickLine={false} axisLine={{ stroke: '#C9CFD9' }} tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: 'rgba(53,102,168,0.06)' }} />
                  <Bar dataKey="production" name="Production" fill={SERIES.production} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  <Bar dataKey="livraisons" name="Livraisons" fill={SERIES.livraisons} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Panel title="Stock actuel par type" action={<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Total <b style={{ fontFamily: tokens.mono, color: tokens.ink }}>{t.stock_final}</b></Typography>} sx={{ height: '100%' }}>
            {stockRows.length === 0 ? <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>Aucun stock</Typography> : stockRows.map((x) => (
              <Box key={x.type} sx={{ display: 'grid', gridTemplateColumns: '64px 1fr 56px', alignItems: 'center', gap: 1.25, py: 0.6 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{x.type}</Typography>
                <Box sx={{ height: 10, bgcolor: '#EEF1F6', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ width: `${(x.stock / stockMax) * 100}%`, height: '100%', bgcolor: SERIES.livrable, borderRadius: 1 }} />
                </Box>
                <Typography sx={{ fontFamily: tokens.mono, fontSize: 13.5, fontWeight: 600, textAlign: 'right' }}>{x.stock}</Typography>
              </Box>
            ))}
          </Panel>
        </Grid>
      </Grid>

      <Panel title={`Détail par type — ${mois}`} flush>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type PBA</TableCell><TableCell align="right">Production</TableCell><TableCell align="right">Livraisons</TableCell>
                <TableCell align="right">Stock actuel</TableCell><TableCell align="right">Rotation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => {
                const rot = item.production > 0 ? (item.livraisons / item.production) * 100 : null;
                return (
                  <TableRow key={item.type} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{item.type}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: item.production ? 'text.primary' : 'text.disabled' }}>{item.production ? `+${item.production}` : 0}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: item.livraisons ? 'text.primary' : 'text.disabled' }}>{item.livraisons ? `−${item.livraisons}` : 0}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{item.stock}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'text.secondary' }}>{rot === null ? '—' : `${rot.toFixed(1).replace('.', ',')} %`}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucune donnée</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
};

export default MonthlyInventory;